import { useEffect, useRef, useState } from 'react';
import {
  Calendar, CheckSquare, CircleDot, FileText, Hash, Link as LinkIcon, LayoutGrid,
  Settings, Trash2, User as UserIcon, X,
} from '../../icons';
import {
  type CustomFieldDto, type CustomFieldOption, type CustomFieldType, type CustomFieldValue,
} from '../../api/task-extras';
import { MemberPickerCell } from './task-cell-popovers';
import { FieldNameEditor } from './FieldNameEditor';
import { tTask, type TaskI18nKey } from '../../lib/task-i18n';

/**
 * AW-002-02 (2026-08-18) — 목록보기의 커스텀 필드 UI 조각들.
 *
 * ① AddFieldMenu — 표 맨 오른쪽 ＋ 가 여는 "커스텀 필드 추가" 메뉴(타입 9종 + 필드 설정).
 * ② FieldCell     — 타입별 값 표시/편집 셀.
 *
 * 화면(TaskTableView)에서 떼어 둔 이유는 타입별 분기가 표 렌더와 섞이면 둘 다 읽기 어려워서다.
 */

export const FIELD_TYPE_ORDER: CustomFieldType[] = [
  'SINGLE_SELECT', 'MULTI_SELECT', 'DATE', 'STATUS', 'TEXT', 'NUMBER', 'URL', 'CHECKBOX', 'MEMBER',
];

/**
 * ⭐⭐ 2026-10-09 (2차 4-5) — ★ 값 타입을 ⭐ `string` ⇒ `TaskI18nKey` 로 ★ 좁혔다.
 *
 * ## ⛔⛔ 왜 — ★ 이 표가 ⇒ ⭐ 「열쇠 아홉이 ★ 없는」 결함을 ★ 두 겹으로 ⭐ 가리고 있었다
 * ```
 * 전  Record<CustomFieldType, ★ string>   ＋  호출부 `tTask(FIELD_TYPE_LABEL[t] ★ as never)`
 * ⇒ ⇒ ⛔ 값이 ★ 아무 글자나 될 수 있고 ⇒ ⭐ `as never` 가 ★ 열쇠 검사를 «끈다»
 *   ⇒ ⇒ ★ 그래서 ⭐ 그 아홉이 `task-i18n` 에 ★ «0건»인 채로 ⇒ ⇒ tsc 가 ⭐ 조용했다
 *     ⇒ ⇒ ⇒ ★ 그리고 `tTask` 는 ⇒ 없는 열쇠에 ⭐ «열쇠 글자»를 돌려주므로
 *       ⇒ ⇒ ★ 메뉴에 ⭐ 「task.field.type_single」이 ★ 그대로 그려졌다
 * 후  Record<CustomFieldType, ⭐ TaskI18nKey>  ＋  호출부에서 ★ `as never` 를 ⭐ 뗐다
 * ⇒ ⇒ ✅ 이제 ★ 열쇠 하나를 지우면 ⇒ ⭐ **tsc 가** 빨개진다(⇐ 관제가 못 박은 관문)
 * ⇒ ⇒ ⛔⛔ 2026-10-10 정정 — ★ 위 한 줄은 ⭐ «거짓»이다. ⇒ 사보타주로 쟀다:
 *   ⇒ task-i18n.ts 에서 `'task.field.type_single'` 선언을 ★ 지우고(남은 선언 0 확인)
 *      ⇒ ⇒ `npx tsc --noEmit -p tsconfig.app.json` ⇒ ⇒ ★ ⭐ EXIT 0 · 오류 ★ 0
 *   ⇒ ★ 까닭 ⇒ ⭐ TaskI18nKey ＝ string 이다 (task-i18n.ts:23 `Record<string, …>`)
 *      ⇒ ⇒ ⚠ 그리고 ★ 같은 파일 ⭐ :250-270 이 ★ 그것을 «이미» 적고 있다
 *         ⇒ ⇒ ⇒ ⭐ 즉 ★ 두 글이 ⭐ 서로 «어긋나 있었다» — ⇒ 이 줄이 ★ 그것을 잇는다
 *   ⇒ ✅ 그러니 ★ 이 아홉의 잠금은 ⇒ ⭐ «시험»이 쥔다 — ⇒ tsc 가 ⛔ 아니다
 *      ⇒ ⇒ (task-i18n.ts:268 ⇒ `pages/ProjectV2/task-board.guard.test.ts` 라 적는다)
 *   ⏳ 만료 ⇒ ★ task-i18n.ts 의 MESSAGES 가 ⭐ `satisfies` 꼴이 되는 날
 *      ⇒ ⇒ ★ 그때 ⭐ 위 ✅ 줄이 ★ «참이 된다» ⇒ ⇒ 이 정정을 지워라
 *      ⇒ ⇒ ⚠ 참고 ⇒ ★ lib/project-i18n 은 ⭐ 2026-10-10(PR 2400)에 ★ 그 꼴이 되었다
 *         ⇒ ⇒ ⇒ 탐침 ⇒ tProject 에 없는 열쇠 ⇒ ★ TS2345 ⭐ 울린다(⇒ 그쪽은 이제 참이다)
 * ```
 * ⇒ ⚠ 이 꼴은 ★ 이 레포에 ⭐ 149 자리가 있었고(인사 세션 전수) ⇒ 그중 ★ 결함은 ⭐ 이 하나였다.
 *   ⇒ ⇒ ★ 나머지 여섯은 ⭐ 열쇠가 다 선언돼 있었고 ⇒ 141 은 ★ «표시글»이라 결함이 아니다.
 * ⇒ ⚠ 그리고 ★ 같은 집안의 ⭐ ⓒ 꼴(`` t(`a.b_${v}` as never) ``)은 ★ 이 파일이 아니라
 *   ⇒ ⇒ `MyTasksBoard` 쪽에 있다 — ⭐ 그것은 ★ 「닫힌 집합을 펴서」 가드가 잠근다.
 */
export const FIELD_TYPE_LABEL: Record<CustomFieldType, TaskI18nKey> = {
  SINGLE_SELECT: 'task.field.type_single',
  MULTI_SELECT: 'task.field.type_multi',
  DATE: 'task.field.type_date',
  STATUS: 'task.field.type_status',
  TEXT: 'task.field.type_text',
  NUMBER: 'task.field.type_number',
  URL: 'task.field.type_url',
  CHECKBOX: 'task.field.type_checkbox',
  MEMBER: 'task.field.type_member',
};

/** 단위 셀렉트의 '직접 입력' 선택값 — 실제 단위 문자열과 겹치지 않게 둔다. */
export const CUSTOM_UNIT = '__custom__';

/** 선택지를 갖는 타입 — 생성 폼이 선택지 입력을 함께 받는다. */
export const SELECT_LIKE = new Set<CustomFieldType>(['SINGLE_SELECT', 'MULTI_SELECT', 'STATUS']);

/**
 * STATUS 타입의 기본 선택지 — 대기 · 진행 · 완료 (2026-08-20).
 *
 * 상태 컬럼을 만들 때 대부분 이 셋을 쓰므로 생성 폼에 미리 채워 넣는다(그대로 고칠 수 있다).
 * 언어를 타므로 상수가 아니라 함수다 — 만들어지는 순간의 언어로 라벨이 저장된다.
 */
export function defaultStatusOptions(): CustomFieldOption[] {
  return parseOptionsText(tTask('task.field.status_defaults'));
}

/** '대기, 진행, 완료' → [{label:'대기'}, …]. 생성 폼 입력과 기본값이 같은 규칙을 쓴다. */
export function parseOptionsText(text: string): CustomFieldOption[] {
  return text.split(',').map((v) => v.trim()).filter(Boolean).map((label) => ({ label }));
}

/** 단위를 갖는 타입 — 지금은 숫자 하나뿐이다. */
export const UNIT_LIKE = new Set<CustomFieldType>(['NUMBER']);

/** 단위 프리셋 — 원 · % · 개 … (언어별). '직접 입력' 은 폼이 따로 붙인다. */
export function unitPresets(): string[] {
  return tTask('task.field.unit_presets').split(',').map((v) => v.trim()).filter(Boolean);
}

/**
 * 숫자 필드의 단위 (2026-08-20).
 *
 * 필드 정의의 `options[0].label` 한 칸에 담는다 — 선택지가 아니라 표시용 부가정보라
 * 값 검증(서버 isValueShapeValid)은 그대로 number 만 받고, 단위는 화면에만 붙는다.
 */
export function unitOf(field: Pick<CustomFieldDto, 'fieldType' | 'options'>): string {
  if (!UNIT_LIKE.has(field.fieldType)) return '';
  return field.options[0]?.label?.trim() ?? '';
}

/** 값 뒤에 단위를 붙인 표시 문자열 — 단위가 없으면 값 그대로. */
export function withUnit(value: number | string, unit: string): string {
  return unit ? `${value} ${unit}` : String(value);
}

/**
 * 셀·폼이 실제로 보여 줄 선택지.
 *
 * 선택지를 비운 채 만든 STATUS 필드는 드롭다운에 '-' 만 있어 아무것도 고를 수 없었다.
 * 그런 필드에 한해 기본값(대기·진행·완료)으로 채운다 — 사용자가 넣은 선택지가 하나라도
 * 있으면 그것이 우선이고, 다른 타입은 손대지 않는다.
 */
export function optionsFor(field: Pick<CustomFieldDto, 'fieldType' | 'options'>): CustomFieldOption[] {
  if (field.fieldType === 'STATUS' && field.options.length === 0) return defaultStatusOptions();
  return field.options;
}

function TypeIcon({ type }: { type: CustomFieldType }) {
  const size = 13;
  switch (type) {
    case 'SINGLE_SELECT':
    case 'MULTI_SELECT': return <LayoutGrid size={size} aria-hidden />;
    case 'DATE': return <Calendar size={size} aria-hidden />;
    case 'STATUS': return <CircleDot size={size} aria-hidden />;
    case 'TEXT': return <FileText size={size} aria-hidden />;
    case 'NUMBER': return <Hash size={size} aria-hidden />;
    case 'URL': return <LinkIcon size={size} aria-hidden />;
    case 'CHECKBOX': return <CheckSquare size={size} aria-hidden />;
    default: return <UserIcon size={size} aria-hidden />;
  }
}

interface AddFieldMenuProps {
  fields: CustomFieldDto[];
  onCreate: (input: { name: string; fieldType: CustomFieldType; options?: CustomFieldOption[] }) => Promise<void>;
  onDelete: (fieldId: string) => Promise<void>;
  /** 숫자 필드의 단위 변경 — 이미 만든 컬럼도 '필드 설정' 에서 단위를 붙이거나 뗄 수 있게 한다. */
  onSetUnit?: (fieldId: string, unit: string) => Promise<void>;
  /** 필드 이름 바꾸기 (2026-09-11) — 넘기면 '필드 설정' 의 이름을 눌러 고칠 수 있다. 없으면 종전처럼 글자만. */
  onRename?: (fieldId: string, name: string) => Promise<void>;
}

/** 표 끝 ＋ 버튼 + 타입 선택 메뉴 + (하단) 커스텀 필드 설정. */
export function AddFieldMenu({ fields, onCreate, onDelete, onSetUnit, onRename }: AddFieldMenuProps) {
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState<CustomFieldType | null>(null);
  const [manage, setManage] = useState(false);
  const [name, setName] = useState('');
  const [optionsText, setOptionsText] = useState('');
  // 숫자 필드의 단위 — 프리셋 선택값. '' = 없음, CUSTOM_UNIT = 직접 입력(unitText 사용).
  const [unit, setUnit] = useState('');
  const [unitText, setUnitText] = useState('');
  const [busy, setBusy] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) close();
    }
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') close(); }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  function close() {
    setOpen(false);
    setPicked(null);
    setManage(false);
    setName('');
    setOptionsText('');
    setUnit('');
    setUnitText('');
  }

  /** 실제로 쓸 단위 — '직접 입력' 이면 입력값, 아니면 고른 프리셋. */
  const resolvedUnit = (unit === CUSTOM_UNIT ? unitText : unit).trim();

  async function submit() {
    if (!picked || busy) return;
    const nm = name.trim();
    if (!nm) return;
    const options = SELECT_LIKE.has(picked)
      ? parseOptionsText(optionsText)
      : UNIT_LIKE.has(picked)
        ? (resolvedUnit ? [{ label: resolvedUnit }] : [])
        : undefined;
    setBusy(true);
    try {
      await onCreate({ name: nm, fieldType: picked, ...(options ? { options } : {}) });
      close();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="tt-addfield" ref={wrapRef}>
      <button
        type="button"
        className="tt-addfield-btn"
        aria-label={tTask('task.field.add_aria')}
        data-tip={tTask('task.field.add_aria')}
        aria-expanded={open}
        onClick={() => (open ? close() : setOpen(true))}
      >
        ＋
      </button>

      {open && (
        <div className="tt-fieldmenu" role="dialog" aria-label={tTask('task.field.add_heading')}>
          {!picked && !manage && (
            <>
              <div className="tt-fieldmenu-head">{tTask('task.field.add_heading')}</div>
              <ul className="tt-fieldmenu-list">
                {FIELD_TYPE_ORDER.map((t) => (
                  <li key={t}>
                    <button
                      type="button"
                      className="tt-fieldmenu-item"
                      onClick={() => {
                        setPicked(t);
                        setName(tTask(FIELD_TYPE_LABEL[t]));
                        setUnit('');
                        setUnitText('');
                        // 상태 필드는 대기·진행·완료 를 미리 채운다 — 그대로 두면 그 셋으로 만들어진다.
                        setOptionsText(t === 'STATUS' ? tTask('task.field.status_defaults') : '');
                      }}
                    >
                      <TypeIcon type={t} />
                      {tTask(FIELD_TYPE_LABEL[t])}
                    </button>
                  </li>
                ))}
              </ul>
              <button type="button" className="tt-fieldmenu-foot" onClick={() => setManage(true)}>
                <Settings size={13} aria-hidden />
                {tTask('task.field.manage')}
              </button>
            </>
          )}

          {picked && (
            <div className="tt-fieldform">
              <div className="tt-fieldmenu-head">
                <TypeIcon type={picked} />
                {tTask(FIELD_TYPE_LABEL[picked])}
                <button type="button" className="tt-fieldform-x" onClick={() => setPicked(null)} aria-label={tTask('task.btn.cancel')} data-tip={tTask('task.btn.cancel')}>
                  <X size={12} aria-hidden />
                </button>
              </div>
              <label className="tt-fieldform-lb" htmlFor="tt-field-name">{tTask('task.field.name_label')}</label>
              <input
                id="tt-field-name"
                className="tt-fieldform-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={60}
                autoFocus
                onKeyDown={(e) => { if (e.key === 'Enter') void submit(); }}
              />
              {UNIT_LIKE.has(picked) && (
                <>
                  <label className="tt-fieldform-lb" htmlFor="tt-field-unit">{tTask('task.field.unit_label')}</label>
                  <select
                    id="tt-field-unit"
                    className="tt-fieldform-input"
                    value={unit}
                    onChange={(e) => setUnit(e.target.value)}
                  >
                    <option value="">{tTask('task.field.unit_none')}</option>
                    {unitPresets().map((u) => <option key={u} value={u}>{u}</option>)}
                    <option value={CUSTOM_UNIT}>{tTask('task.field.unit_custom')}</option>
                  </select>
                  {unit === CUSTOM_UNIT && (
                    <input
                      className="tt-fieldform-input"
                      value={unitText}
                      onChange={(e) => setUnitText(e.target.value)}
                      maxLength={10}
                      placeholder={tTask('task.field.unit_ph')}
                      onKeyDown={(e) => { if (e.key === 'Enter') void submit(); }}
                    />
                  )}
                </>
              )}
              {SELECT_LIKE.has(picked) && (
                <>
                  <label className="tt-fieldform-lb" htmlFor="tt-field-options">{tTask('task.field.options_label')}</label>
                  <input
                    id="tt-field-options"
                    className="tt-fieldform-input"
                    value={optionsText}
                    onChange={(e) => setOptionsText(e.target.value)}
                    placeholder={tTask('task.field.options_ph')}
                    onKeyDown={(e) => { if (e.key === 'Enter') void submit(); }}
                  />
                </>
              )}
              <button type="button" className="tt-fieldform-submit" disabled={busy || !name.trim()} onClick={() => void submit()}>
                {busy ? tTask('task.btn.saving') : tTask('task.field.create')}
              </button>
            </div>
          )}

          {manage && (
            <div className="tt-fieldmanage">
              <div className="tt-fieldmenu-head">
                {tTask('task.field.manage')}
                <button type="button" className="tt-fieldform-x" onClick={() => setManage(false)} aria-label={tTask('task.btn.close')} data-tip={tTask('task.btn.close')}>
                  <X size={12} aria-hidden />
                </button>
              </div>
              {fields.length === 0 && <p className="tt-fieldmanage-empty">{tTask('task.field.manage_empty')}</p>}
              <ul className="tt-fieldmenu-list">
                {fields.map((f) => (
                  <li key={f.id} className="tt-fieldmanage-row">
                    <TypeIcon type={f.fieldType} />
                    {onRename
                      ? <FieldNameEditor field={f} onRename={onRename} />
                      : <span className="tt-fieldmanage-name">{f.name}</span>}
                    {onSetUnit && UNIT_LIKE.has(f.fieldType) && (
                      <select
                        className="tt-fieldmanage-unit"
                        aria-label={`${f.name} ${tTask('task.field.unit_label')}`}
                        value={unitPresets().includes(unitOf(f)) || unitOf(f) === '' ? unitOf(f) : CUSTOM_UNIT}
                        onChange={(e) => {
                          const v = e.target.value;
                          // 프리셋에 없는 단위를 쓰던 필드는 그 값을 그대로 두고 목록에만 '직접 입력' 으로 보인다.
                          if (v === CUSTOM_UNIT) return;
                          void onSetUnit(f.id, v);
                        }}
                      >
                        <option value="">{tTask('task.field.unit_none')}</option>
                        {unitPresets().map((u) => <option key={u} value={u}>{u}</option>)}
                        {!unitPresets().includes(unitOf(f)) && unitOf(f) !== '' && (
                          <option value={CUSTOM_UNIT}>{unitOf(f)}</option>
                        )}
                      </select>
                    )}
                    <button
                      type="button"
                      className="tt-fieldmanage-del"
                      aria-label={tTask('task.field.delete_aria', { name: f.name })}
                      onClick={() => { void onDelete(f.id); }}
                    >
                      <Trash2 size={12} aria-hidden />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * select 가 붙는 순간 선택지 목록까지 펼친다(2026-08-20). 클릭 한 번에 '대기·진행·완료' 가
 * 보이게 하려는 것 — showPicker 는 사용자 제스처 밖에서 호출하면 던지므로 감싸 둔다.
 */
function openPickerRef(el: HTMLSelectElement | null) {
  if (!el) return;
  try {
    (el as HTMLSelectElement & { showPicker?: () => void }).showPicker?.();
  } catch {
    /* 미지원·제스처 밖 — autoFocus 만으로 충분하다. */
  }
}

interface FieldCellProps {
  field: CustomFieldDto;
  value: CustomFieldValue;
  /** 프로젝트 팀 멤버 — MEMBER 타입 셀의 선택지. */
  members: Array<{ userId: string; name: string }>;
  onChange: (next: CustomFieldValue) => void;
  disabled?: boolean;
}

/** 타입별 값 셀 — 표시는 항상, 편집은 클릭 시작. */
export function FieldCell({ field, value, members, onChange, disabled }: FieldCellProps) {
  const [editing, setEditing] = useState(false);

  if (field.fieldType === 'CHECKBOX') {
    return (
      <input
        type="checkbox"
        className="tt-cell-check"
        checked={value === true}
        disabled={disabled}
        aria-label={field.name}
        onChange={(e) => onChange(e.target.checked)}
      />
    );
  }

  /*
    MEMBER 는 편집 단계 없이 곧바로 팝오버로 고른다(2026-08-21 상사 지적).
    종전에는 셀을 눌러 편집 상태로 만든 뒤 native <select> 가 떠서, 같은 표의 담당자 칸
    (AssigneeCell)과 생김새가 달랐고 검색도 없었다. 이제 담당자 칸과 같은 팝오버를 쓴다.
  */
  if (field.fieldType === 'MEMBER') {
    return (
      <MemberPickerCell
        value={typeof value === 'string' && value !== '' ? value : null}
        members={members}
        label={field.name}
        disabled={disabled}
        onChange={(userId) => onChange(userId)}
      />
    );
  }

  if (!editing) {
    const empty = value === null || value === undefined || value === '' || (Array.isArray(value) && value.length === 0);
    return (
      <button
        type="button"
        className={`tt-cell-view${empty ? ' tt-cell-view--empty' : ''}`}
        disabled={disabled}
        onClick={() => setEditing(true)}
        data-tip={field.name}
      >
        {empty ? '-' : renderValue(field, value, members)}
      </button>
    );
  }

  const commit = (next: CustomFieldValue) => { onChange(next); setEditing(false); };

  switch (field.fieldType) {
    case 'NUMBER': {
      const unit = unitOf(field);
      return (
        <span className="tt-cell-num">
          <input
            type="number"
            className="tt-cell-input"
            defaultValue={typeof value === 'number' ? value : ''}
            autoFocus
            onBlur={(e) => commit(e.target.value === '' ? null : Number(e.target.value))}
            onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); if (e.key === 'Escape') setEditing(false); }}
          />
          {unit && <em className="tt-cell-unit">{unit}</em>}
        </span>
      );
    }
    case 'DATE':
      return (
        <input
          type="date"
          className="tt-cell-input"
          defaultValue={typeof value === 'string' ? value : ''}
          autoFocus
          onBlur={(e) => commit(e.target.value || null)}
          onKeyDown={(e) => { if (e.key === 'Escape') setEditing(false); }}
        />
      );
    case 'SINGLE_SELECT':
    case 'STATUS':
      return (
        <select
          className="tt-cell-input"
          defaultValue={typeof value === 'string' ? value : ''}
          autoFocus
          // 셀을 한 번 누르면 선택지가 바로 펼쳐지게 — 지원 안 하는 브라우저는 포커스만 되고 조용히 넘어간다.
          ref={openPickerRef}
          onChange={(e) => commit(e.target.value || null)}
          onBlur={() => setEditing(false)}
        >
          <option value="">-</option>
          {optionsFor(field).map((o) => <option key={o.label} value={o.label}>{o.label}</option>)}
        </select>
      );
    case 'MULTI_SELECT': {
      const arr = Array.isArray(value) ? value : [];
      return (
        <div className="tt-cell-multi">
          {field.options.map((o) => (
            <label key={o.label} className="tt-cell-multi-item">
              <input
                type="checkbox"
                checked={arr.includes(o.label)}
                onChange={(e) => {
                  const next = e.target.checked ? [...arr, o.label] : arr.filter((v) => v !== o.label);
                  onChange(next);
                }}
              />
              {o.label}
            </label>
          ))}
          <button type="button" className="tt-cell-multi-done" onClick={() => setEditing(false)}>
            {tTask('task.btn.close')}
          </button>
        </div>
      );
    }
    default:
      return (
        <input
          type={field.fieldType === 'URL' ? 'url' : 'text'}
          className="tt-cell-input"
          defaultValue={typeof value === 'string' ? value : ''}
          autoFocus
          maxLength={500}
          onBlur={(e) => commit(e.target.value.trim() || null)}
          onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); if (e.key === 'Escape') setEditing(false); }}
        />
      );
  }
}

function renderValue(
  field: CustomFieldDto,
  value: CustomFieldValue,
  members: Array<{ userId: string; name: string }>,
) {
  if (field.fieldType === 'MEMBER') {
    const m = members.find((x) => x.userId === value);
    return m?.name ?? String(value);
  }
  if (Array.isArray(value)) {
    return (
      <span className="tt-chips">
        {value.map((v) => <span key={v} className="tt-chip">{v}</span>)}
      </span>
    );
  }
  if (field.fieldType === 'SINGLE_SELECT' || field.fieldType === 'STATUS') {
    const opt = field.options.find((o) => o.label === value);
    return (
      <span className="tt-chip" style={opt?.color ? { background: opt.color, color: '#fff' } : undefined}>
        {String(value)}
      </span>
    );
  }
  if (field.fieldType === 'NUMBER') {
    // 단위가 있으면 값 뒤에 붙인다 — '120 개' 처럼. 저장값은 숫자 그대로다.
    return withUnit(String(value), unitOf(field));
  }
  if (field.fieldType === 'URL') {
    return <span className="tt-cell-url">{String(value)}</span>;
  }
  return String(value);
}
